import { getLanguageName } from './transformers';

/**
 * The feed's API story, mapped to the shape the rows render.
 *
 * Extracted from the `storiesList` useMemo in StoriesFeed because it is a
 * STRICT WHITELIST and that is a trap. Anything added to `/stories_search` and
 * not added here is silently dropped between the server and the row, and the
 * component then renders `undefined` without complaining.
 *
 * That is not hypothetical. The Trending marker shipped to production dead on
 * 25 October 2026: the backend attached `signal`, the row read `story.signal`,
 * and this mapping in the middle threw it away. Nothing failed, no test broke,
 * and the feature simply did not appear. The same class of bug is recorded
 * twice more in CLAUDE.md, where `skipBucketReformatting` had to be added to
 * two separate whitelists for exactly this reason.
 *
 * It lives in its own module so `feedStoryShape.test.js` can import it without
 * dragging in redux, MUI and the whole component tree, which is why the
 * original had no test at all.
 *
 * KEEP THIS IN STEP with what the rows read. If you add a field to the feed
 * payload, add it here and add it to the test's survival list.
 */
export function mapFeedStory(s) {
  const rawTitle = s.title || s.groupSubject?.title;
  const title = (rawTitle && typeof rawTitle === 'object') ? rawTitle.text : rawTitle;
  const rawExcerpt = s.excerpt;
  const excerpt = (rawExcerpt && typeof rawExcerpt === 'object') ? rawExcerpt.text : rawExcerpt;
  const totalArticleCount = s.totalArticleCount || s.articleCount || s.groupSubject?.articleCount;
  const matchedArticleCount = s.matchedArticleCount || (s.articles || []).length;
  return {
    _id: s.groupSubject._id,
    slug: s.groupSubject?.slug,
    title,
    totalArticleCount,
    matchedArticleCount,
    // "This subset may be a truncated prefix", which the article page
    // needs in order to tell an exclusion from an unexplained absence.
    articlesTruncated: s.articlesTruncated === true,
    articles: (s.articles || []).map((a) => ({ ...a, storyTitle: title })),
    earliest: s.earliest,
    recent: s.recent,
    languages: [...new Set(s.languageNames || (s.languagesCodes || []).map(getLanguageName))].join(', '),
    excerpt,
    rawExcerpt: s.excerpt,
    maxRanking: s.maxRanking,
    dominantSentiment: s.dominantSentiment,
    totalShares: s.totalShares,
    articlesWithRanking: s.articlesWithRanking,
    feedSources: s.feedSources,
    articleCount: totalArticleCount,
    // The Signal evidence, or null for an ordinary story. Null rather than
    // undefined so a row can tell "not a Signal" from "the server did not say",
    // which matters while an older backend may still be serving the feed during
    // a deploy.
    signal: s.signal || null,
    type: 'story',
  };
}

export default mapFeedStory;
